<template>
  <modal
    title="Notify customer of schedule change?"
    class-name="small-modal"
    @hide-modal="handleDismiss"
  >
    <p class="mb-2">
      {{ removedTechName }} has been removed from this appointment. The
      appointment remains assigned to the other technician(s).
    </p>
    <p>Would you like to notify the customer of this change?</p>
    <template #footer>
      <div class="modal-footer d-flex justify-content-end" style="gap: 0.5rem">
        <SeraButton
          type="secondary"
          data-cy="dont-notify-btn"
          :disabled="saving"
          @click="handleDontNotify"
        >
          Don't Notify
        </SeraButton>
        <SeraButton
          data-cy="notify-btn"
          :disabled="saving"
          @click="handleNotify"
        >
          <span v-if="!saving">Notify Customer</span>
          <Spinner v-else message="Saving..." />
        </SeraButton>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";

interface TimePayload {
  arrival_window_start: string;
  arrival_window_end: string;
  scheduled_time: string;
}

interface PendingPayload {
  appointmentId: string | number;
  technicianProfileIds: number[];
  timePayload: TimePayload | null;
}

const props = defineProps<{
  pendingPayload: PendingPayload;
  removedTechName: string;
}>();

const emit = defineEmits<{
  "hide-modal": [];
}>();

const store = useStore();
const saving = ref(false);
let resolved = false;

const submitUpdate = async (skipNotifications: boolean) => {
  if (resolved) return;
  resolved = true;
  saving.value = true;
  try {
    const { appointmentId, technicianProfileIds, timePayload } =
      props.pendingPayload;

    if (timePayload) {
      await store.dispatch("updateAppointmentEvent", {
        appointmentId,
        skip_notifications: skipNotifications,
        ...timePayload
      });
    }

    await store.dispatch("assignAppointmentEvent", {
      appointmentId,
      technician_profile_ids: technicianProfileIds,
      skip_notifications: skipNotifications
    });

    window.notify.success("Appointment updated");
  } catch {
    window.notify.error("Failed to update appointment");
  } finally {
    saving.value = false;
    emit("hide-modal");
  }
};

const handleNotify = () => submitUpdate(false);
const handleDontNotify = () => submitUpdate(true);
const handleDismiss = () => submitUpdate(true);

defineExpose({ handleDismiss, saving });
</script>
